/**
 * General text formatting utilities
 *
 * Provides basic text cleanup functions used across other formatters
 */

import type { FormattingResult } from './types';

/**
 * Trim and normalize whitespace
 * Removes leading/trailing spaces, collapses multiple spaces to single
 *
 * @example
 * normalizeWhitespace('  John   Smith  ')
 * // { formatted: 'John Smith', changed: true }
 */
export function normalizeWhitespace(value: string): FormattingResult {
  if (!value) {
    return { formatted: '', changed: false };
  }

  const formatted = value
    .trim()
    .replace(/\s+/g, ' ');

  return {
    formatted,
    changed: formatted !== value,
  };
}

/**
 * Capitalize first letter of a string
 */
function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Capitalize first letter of each word
 * Respects special characters and apostrophes
 *
 * @example
 * capitalizeWords('john smith') // { formatted: 'John Smith', changed: true }
 * capitalizeWords("o'brien") // { formatted: "O'Brien", changed: true }
 */
export function capitalizeWords(value: string): FormattingResult {
  if (!value) {
    return { formatted: '', changed: false };
  }

  // Handle words split by spaces, hyphens, and apostrophes
  const formatted = value
    .split(/(\s|-|')/)
    .map((part, index, parts) => {
      // Keep delimiters (spaces, hyphens, apostrophes) as-is
      if (part === ' ' || part === '-' || part === "'") {
        return part;
      }

      // Capitalize first character if it's after an apostrophe or at start/after space/hyphen
      if (part.length > 0) {
        const prevPart = index > 0 ? parts[index - 1] : '';

        // After apostrophe: capitalize (O'Brien)
        if (prevPart === "'") {
          return capitalize(part);
        }

        // After space or hyphen or start: capitalize
        if (prevPart === ' ' || prevPart === '-' || index === 0) {
          return capitalize(part);
        }

        // Otherwise lowercase
        return part.toLowerCase();
      }

      return part;
    })
    .join('');

  return {
    formatted,
    changed: formatted !== value,
  };
}

/**
 * Capitalize only the first letter (sentence case)
 *
 * @example
 * capitalizeSentence('hello world') // { formatted: 'Hello world', changed: true }
 */
export function capitalizeSentence(value: string): FormattingResult {
  if (!value) {
    return { formatted: '', changed: false };
  }

  const formatted = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

  return {
    formatted,
    changed: formatted !== value,
  };
}

/**
 * Words that should remain lowercase in title case (unless at start)
 */
const LOWERCASE_WORDS = [
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'nor', 'of',
  'on', 'or', 'the', 'to', 'up',
];

/**
 * Convert to title case
 * Capitalizes major words, keeps minor words lowercase
 *
 * @example
 * toTitleCase('the quick brown fox')
 * // { formatted: 'The Quick Brown Fox', changed: true }
 *
 * toTitleCase('a tale of two cities')
 * // { formatted: 'A Tale of Two Cities', changed: true }
 */
export function toTitleCase(value: string): FormattingResult {
  if (!value) {
    return { formatted: '', changed: false };
  }

  const words = value.toLowerCase().split(' ');

  const formatted = words
    .map((word, index) => {
      // Always capitalize first and last word
      if (index === 0 || index === words.length - 1) {
        return capitalize(word);
      }

      // Keep minor words lowercase
      if (LOWERCASE_WORDS.includes(word)) {
        return word;
      }

      // Capitalize major words
      return capitalize(word);
    })
    .join(' ');

  return {
    formatted,
    changed: formatted !== value,
  };
}

/**
 * Detect if text is all caps (likely unintentional)
 * Ignores strings shorter than minLength (acronyms like "IBM" are ok)
 *
 * @example
 * isAllCaps('JOHN SMITH') // true
 * isAllCaps('John SMITH') // false
 * isAllCaps('IBM') // false (too short, likely intentional)
 */
export function isAllCaps(value: string, minLength: number = 10): boolean {
  if (!value || value.length < minLength) {
    return false;
  }

  // Count letters only (ignore numbers, spaces, punctuation)
  const letters = value.replace(/[^a-zA-Z]/g, '');

  if (letters.length === 0) {
    return false;
  }

  // Check if all letters are uppercase
  return letters === letters.toUpperCase() && letters.length >= minLength;
}

/**
 * Clean input text (combines multiple operations)
 * Standard cleanup: trim, normalize whitespace, basic formatting
 *
 * @example
 * cleanText('  john   smith  ')
 * // { formatted: 'John Smith', changed: true }
 */
export function cleanText(value: string): FormattingResult {
  if (!value) {
    return { formatted: '', changed: false };
  }

  // Normalize whitespace first
  const normalized = normalizeWhitespace(value);

  // Detect if all caps
  const allCaps = isAllCaps(normalized.formatted);

  // If all caps, apply title case, otherwise just return normalized
  if (allCaps) {
    const titleCased = capitalizeWords(normalized.formatted);
    return {
      formatted: titleCased.formatted,
      changed: true,
      warnings: ['All-caps text was converted to title case'],
    };
  }

  return normalized;
}
